import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Spin, Alert } from 'antd';
import { IconHistory } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getAdminAuditLog, IAdminAuditLogEntry } from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, dateSorter, uniqueValueFilters, stringOnFilter } from '../../../helpers/adminTableHelpers';

const { Title } = Typography;

// Immutable log of every admin action taken anywhere in this panel -- every
// other section writes here automatically (AdminAuditLogHelper), there is
// nothing to create/edit/delete from this screen.
const AuditLogSection: React.FC = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<IAdminAuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getAdminAuditLog()
      .then(setEntries)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const adminNameFilters = useMemo(() => uniqueValueFilters(entries, 'adminName'), [entries]);
  const actionFilters = useMemo(() => uniqueValueFilters(entries, 'action'), [entries]);

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_AUDIT_LOG)}
      </Title>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_LOAD_ERROR)} />
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<IconHistory size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminAuditLogEntry>
          rowKey="id"
          dataSource={entries}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.id,
            title: record.action,
            subtitle: record.targetLabel,
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_COL_ADMIN), value: record.adminName },
              { label: translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_COL_DATE), value: new Date(record.createdAt).toLocaleString() },
              { label: translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_COL_REASON), value: record.reason, wide: true },
            ],
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_COL_DATE),
              dataIndex: 'createdAt',
              key: 'createdAt',
              sorter: dateSorter<IAdminAuditLogEntry>('createdAt'),
              defaultSortOrder: 'descend',
              render: (value: string) => new Date(value).toLocaleString(),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_COL_ADMIN),
              dataIndex: 'adminName',
              key: 'adminName',
              sorter: textSorter<IAdminAuditLogEntry>('adminName'),
              filters: adminNameFilters,
              onFilter: stringOnFilter<IAdminAuditLogEntry>('adminName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_COL_ACTION),
              dataIndex: 'action',
              key: 'action',
              sorter: textSorter<IAdminAuditLogEntry>('action'),
              filters: actionFilters,
              onFilter: stringOnFilter<IAdminAuditLogEntry>('action'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_COL_TARGET),
              dataIndex: 'targetLabel',
              key: 'targetLabel',
              sorter: textSorter<IAdminAuditLogEntry>('targetLabel'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_AUDIT_LOG_COL_REASON),
              dataIndex: 'reason',
              key: 'reason',
              render: (value?: string) => value ?? '—',
            },
          ]}
        />
      )}
    </div>
  );
};

export default AuditLogSection;
